import {
  SearchCriteria,
  formatDataObject,
  getGenericEntityListingAPI,
  getCommunicationLogCasesByIdAPI,
  DataTypeStore,
  GetGenericEntityListingApiType,
} from '@app-store'
import { FieldValues } from 'react-hook-form'
import { SORT_KEYS } from '@ui-kit'
import { ExportPayloadType } from '../types/correspondenceLogs.types'
import {
  ACCESSOR_TO_API_FIELD,
  ACCESSOR_TO_PARENT_ENTITY,
  ExportRelatedEntityConfig,
  IntegrationType,
  OcrChildFaxIntegrationLogsContext,
  ROOT_ENTITY,
  SortDirectionState,
} from '../types/types'
import {
  CTI_ACCESSOR_TO_API_FIELD,
  CTI_ACCESSOR_TO_PARENT_ENTITY,
  CTI_ROOT_ENTITY,
  INTEGRATION_LOGS,
} from './constants'
import { formatLocalDateWithZone } from './date.utils'
import {
  GenericPayloadListingFilters,
  IntegrationLogsRelatedFiltersBuilder,
} from './types'

export const getApiFieldName = (searchKey: string): string => {
  const accessor = searchKey.toLowerCase()
  return ACCESSOR_TO_API_FIELD[accessor] ?? accessor
}

export const getCtiApiFieldName = (searchKey: string): string => {
  const accessor = searchKey.toLowerCase()
  return CTI_ACCESSOR_TO_API_FIELD[accessor] ?? accessor
}

export function getParentEntity(accessor: string): string {
  const key = accessor?.toLowerCase()
  return ACCESSOR_TO_PARENT_ENTITY[key] ?? ROOT_ENTITY
}

export function getCtiParentEntity(accessor: string): string {
  const key = accessor?.toLowerCase()
  return CTI_ACCESSOR_TO_PARENT_ENTITY[key] ?? CTI_ROOT_ENTITY
}

export const getFieldNameForIntegration = (
  searchKey: string,
  isCti: boolean
): string =>
  isCti ? getCtiApiFieldName(searchKey) : getApiFieldName(searchKey)

export const getParentEntityForIntegration = (
  searchKey: string,
  isCti: boolean
): string =>
  isCti ? getCtiParentEntity(searchKey) : getParentEntity(searchKey)

export const getRootEntityForIntegration = (isCti: boolean): string =>
  isCti ? CTI_ROOT_ENTITY : ROOT_ENTITY

export const INTEGRATION_LOGS_EXPORT_BASE = {
  entity_name: 'generic_payload',
  select: [
    'status',
    'http_status',
    'request_id',
    'created_at',
    'modified_at',
    'created_by',
    'case',
    'patient',
    'integration_functionality',
    'case_ids',
  ],
  related_entities: {
    case: {
      select: ['case_key', 'programs', 'products'],
      related_entities: {
        programs: {
          select: ['operational_program_name'],
          ui_data_mappings: {
            operational_program_name: {
              alias: ['Program'],
              queryOrder: [10],
            },
          },
          exclude_fields: ['record_id', 'entity_type', 'category'],
        },
        products: {
          select: ['trademarked_name'],
          ui_data_mappings: {
            trademarked_name: {
              alias: ['Product'],
              queryOrder: [11],
            },
          },
          exclude_fields: ['record_id', 'entity_type', 'category'],
        },
      },
      ui_data_mappings: {
        case_key: {
          alias: ['Case ID'],
          queryOrder: [9],
        },
      },
      exclude_fields: ['record_id', 'entity_type', 'category', 'name'],
    },
    patient: {
      select: ['patient_key'],
      ui_data_mappings: {
        patient_key: {
          alias: ['Entity ID'],
          queryOrder: [8],
        },
      },
      exclude_fields: ['record_id', 'entity_type', 'category', 'name'],
    },
    integration_functionality: {
      select: ['identifier', 'request_type'],
      ui_data_mappings: {
        identifier: {
          alias: ['API Identifier'],
          queryOrder: [1],
        },
        request_type: {
          alias: ['Request Type'],
          queryOrder: [4],
        },
      },
    },
  },
  ui_data_mappings: {
    request_id: {
      alias: ['Transaction ID'],
      queryOrder: [5],
    },
    status: {
      alias: ['Status'],
      queryOrder: [2],
    },
    http_status: {
      alias: ['HTTPS Status Code'],
      queryOrder: [3],
    },
    created_at: {
      alias: ['Request Timestamp'],
      queryOrder: [6],
    },
    modified_at: {
      alias: ['Response Timestamp'],
      queryOrder: [7],
    },
    created_by: {
      alias: ['Triggered By'],
      queryOrder: [12],
    },
    case_ids: {
      alias: ['Case ID'],
      queryOrder: [13],
    },
  },
  exclude_fields: ['record_id', 'entity_type', 'category'],
} as Omit<ExportPayloadType, 'filters'>

export const buildIntegrationLogsExportPayload = (
  searchCriteria: SearchCriteria[],
  groupId: string,
  sort?: SortDirectionState
): ExportPayloadType => {
  const validCriteria = searchCriteria.filter(
    (criteria) => criteria && Object.keys(criteria).length > 0
  )

  const rootFilters: formatDataObject[] = []
  const relatedFiltersByEntity: Record<string, formatDataObject[]> = {}

  validCriteria.forEach((criteria) => {
    const searchKey = criteria?.search_key ?? ''
    const parentEntity = getParentEntity(searchKey)

    const filter: formatDataObject = {
      operation: criteria?.operator ?? 'EQUALS',
      field_name: getApiFieldName(searchKey),
      values: criteria?.search_value,
    }

    if (parentEntity === ROOT_ENTITY) {
      rootFilters.push(filter)
    } else {
      ;(relatedFiltersByEntity[parentEntity] ??= []).push(filter)
    }
  })

  const isSortActive =
    sort?.direction !== undefined &&
    sort.direction !== SORT_KEYS.Disabled &&
    !!sort.keyName

  const sortEntity = isSortActive ? getParentEntity(sort?.key ?? '') : null

  const sortCriteria: formatDataObject[] = isSortActive
    ? [
        {
          direction: sort?.direction.toUpperCase(),
          field_name: sort?.keyName ?? '',
        },
      ]
    : []

  const buildRelatedFilters = (
    entity: string
  ): Pick<ExportRelatedEntityConfig, 'filters'> => {
    const criteria = relatedFiltersByEntity[entity] ?? []

    const filterBlocks: { criteria: formatDataObject[] }[] = []
    if (criteria.length) filterBlocks.push({ criteria })
    if (sortEntity === entity && sortCriteria.length) {
      filterBlocks.push({ criteria: sortCriteria })
    }

    return filterBlocks.length ? { filters: filterBlocks } : {}
  }

  const filters: object[] = [
    {
      logical_operator: 'AND',
      criteria: [
        ...rootFilters,
        {
          operation: 'EQUALS',
          field_name: 'group_id',
          values: [groupId],
        },
      ],
    },
    {
      criteria: sortEntity === ROOT_ENTITY ? sortCriteria : [],
    },
  ]

  const baseRelated = INTEGRATION_LOGS_EXPORT_BASE.related_entities as Record<
    string,
    ExportRelatedEntityConfig
  >

  const related_entities = {
    ...baseRelated,
    case: {
      ...baseRelated.case,
      ...buildRelatedFilters('case'),
      related_entities: {
        ...baseRelated.case?.related_entities,
        programs: {
          ...baseRelated.case?.related_entities?.programs,
          ...buildRelatedFilters('programs'),
        },
        products: {
          ...baseRelated.case?.related_entities?.products,
          ...buildRelatedFilters('products'),
        },
      },
    },
    integration_functionality: {
      ...baseRelated.integration_functionality,
      ...buildRelatedFilters('integration_functionality'),
    },
    patient: {
      ...baseRelated.patient,
      ...buildRelatedFilters('patient'),
    },
  }

  return {
    ...INTEGRATION_LOGS_EXPORT_BASE,
    related_entities,
    filters,
  } as unknown as ExportPayloadType
}

export const formatJson = (value: Record<string, unknown>): string => {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

export const isCtiIntegration = (
  integration?: IntegrationType | null
): boolean => {
  if (!integration) return false
  const label = (integration.label ?? '').trim().toLowerCase()
  const identifier = (integration.identifier ?? '').trim().toLowerCase()
  return (
    integration.value === INTEGRATION_LOGS.CTI_ENTITY_NAME ||
    identifier === INTEGRATION_LOGS.CTI_LABEL.toLowerCase() ||
    label === INTEGRATION_LOGS.CTI_LABEL.toLowerCase()
  )
}

export const isIvrIntegration = (
  integration?: IntegrationType | null
): boolean => {
  if (!integration) return false
  const label = (integration.label ?? '').trim().toLowerCase()
  const identifier = (integration.identifier ?? '').trim().toLowerCase()
  const ivrKey = INTEGRATION_LOGS.IVR_GROUP_ID.toLowerCase()
  return identifier === ivrKey || label === ivrKey
}

const getNestedRelationValue = (
  relation: FieldValues | FieldValues[] | null | undefined,
  field: string
): string | undefined => {
  if (!relation) return undefined
  if (Array.isArray(relation)) {
    return relation.find((entry) => entry?.[field])?.[field]
  }
  return relation?.[field]
}

export const getProcessingDurationSeconds = (
  requestTimestamp: string | Date | null | undefined,
  responseTimestamp: string | Date | null | undefined
): string => {
  if (!requestTimestamp || !responseTimestamp) return '--'

  const requestMs = new Date(requestTimestamp).getTime()
  const responseMs = new Date(responseTimestamp).getTime()

  if (Number.isNaN(requestMs) || Number.isNaN(responseMs)) return '--'

  const diffMs = responseMs - requestMs
  if (diffMs < 0) return '--'

  return `${(diffMs / 1000).toFixed(2)} s`
}

export interface CommunicationLogCase {
  id?: number
  case_key?: string
  services?: string
}

export interface IntegrationLogCaseId {
  case_id?: number
  case_key?: string
}

export const normalizeIntegrationLogCaseIds = (
  caseIds: unknown
): IntegrationLogCaseId[] => {
  if (caseIds === null || caseIds === undefined) return []

  if (typeof caseIds === 'string') {
    const trimmed = caseIds.trim()
    if (!trimmed) return []
    try {
      return normalizeIntegrationLogCaseIds(JSON.parse(trimmed))
    } catch {
      return []
    }
  }

  if (!Array.isArray(caseIds)) return []

  return caseIds as IntegrationLogCaseId[]
}

export const toCommunicationLogCases = (
  caseIds: unknown
): CommunicationLogCase[] => {
  return normalizeIntegrationLogCaseIds(caseIds)
    .filter((caseItem) => Boolean(caseItem?.case_key))
    .map((caseItem) => ({
      id: caseItem.case_id,
      case_key: caseItem.case_key,
    }))
}

export const getCommunicationRecordIdFromLogItem = (
  item: FieldValues
): string | number | undefined => {
  const recordId = item?.resource_id
  if (recordId === null || recordId === undefined || recordId === '') {
    return undefined
  }
  return recordId
}

export const mergeGenericPayloadLogItems = (
  primary: FieldValues[],
  supplemental: FieldValues[]
): FieldValues[] => {
  const seen = new Set<string>()
  const merged: FieldValues[] = []

  ;[...primary, ...supplemental].forEach((item) => {
    const id = item?.id
    if (id !== null && id !== undefined && id !== '') {
      const key = String(id)
      if (seen.has(key)) return
      seen.add(key)
    }
    merged.push(item)
  })

  return merged
}

export const fetchGenericPayloadIntegrationLogsListing = (
  page: number,
  size: number,
  filterPayload: GenericPayloadListingFilters,
  buildRelatedFilters: IntegrationLogsRelatedFiltersBuilder,
  caseExtraCriteria: formatDataObject[] = []
) =>
  getGenericEntityListingAPI({
    select: [
      'id',
      'status',
      'http_status',
      'request_body_json',
      'response_body_json',
      'response_error_message',
      'request_id',
      'created_at',
      'created_by',
      'case',
      'patient',
      'modified_at',
      'group_id',
      'integration_functionality',
      'resource_id',
      'communication',
      'case_ids',
    ],
    page,
    size,
    filters: filterPayload,
    entity_name: 'generic_payload',
    related_entities: {
      case: {
        select: ['case_key', 'id', 'programs', 'products'],
        ...buildRelatedFilters('case', caseExtraCriteria),
        related_entities: {
          programs: {
            select: ['operational_program_name'],
            ...buildRelatedFilters('programs'),
          },
          products: {
            select: ['generic_name', 'trademarked_name'],
            ...buildRelatedFilters('products'),
          },
        },
      },
      integration_functionality: {
        select: ['integration_name', 'request_type', 'identifier'],
        ...buildRelatedFilters('integration_functionality'),
      },
      patient: {
        select: ['patient_key'],
        ...buildRelatedFilters('patient'),
      },
      communication: {
        select: ['communication_id'],
        ...buildRelatedFilters('communication'),
      },
    },
  })

export const resolveOcrChildFaxContextForCase = async (
  caseId: string | number
): Promise<OcrChildFaxIntegrationLogsContext> => {
  const notApplicable: OcrChildFaxIntegrationLogsContext = { applies: false }

  try {
    const response = await getGenericEntityListingAPI({
      entity_name: 'communication',
      select: [
        'id',
        'parent_communication_id',
        'case_id',
        'ocr_outbound_batch',
      ],
      page: 0,
      size: 10,
      filters: [
        {
          criteria: [
            {
              field_name: 'case_id',
              operation: 'EQUALS',
              values: [String(caseId)],
            },
          ],
        },
      ],
    })

    const row = response.content.find(
      (item: FieldValues) =>
        item?.ocr_outbound_batch && item?.parent_communication_id
    ) as FieldValues | undefined

    if (!row) {
      return notApplicable
    }

    return {
      applies: true,
      parentCommunicationRecordId: row.parent_communication_id,
      communicationRecordId: row.id,
    }
  } catch (err) {
    console.error('[Integration Logs] resolveOcrChildFaxContextForCase:', err)
    return notApplicable
  }
}

export const mapCtiIntegrationRow = (item: FieldValues): DataTypeStore => {
  const communication = item?.communication as FieldValues | undefined

  return {
    id: item?.id,
    req: {
      response: item?.response_payload,
      request: item?.request_payload,
    },
    api_identifier: item?.api_identifier || '--',
    call_received_at: item?.call_received_at
      ? formatLocalDateWithZone(item.call_received_at as string)
      : '--',
    aws_contact_id: item?.aws_contact_id || '--',
    program:
      getNestedRelationValue(
        communication?.program ?? communication?.programs,
        CTI_ACCESSOR_TO_API_FIELD.program
      ) || '--',
    product:
      getNestedRelationValue(
        communication?.product ?? communication?.products,
        CTI_ACCESSOR_TO_API_FIELD.product
      ) || '--',
    interaction_id: communication?.communication_id || '--',
    communication_record_id: communication?.id,
  } as unknown as DataTypeStore
}

export const normalizeCtiListingResponse = (
  data: unknown
): {
  content: FieldValues[]
  totalPages: number
  totalElements: number
  numberOfElements: number
} => {
  if (Array.isArray(data)) {
    const content = data as FieldValues[]
    return {
      content,
      totalPages: 1,
      totalElements: content.length,
      numberOfElements: content.length,
    }
  }

  const paginated = data as {
    content?: FieldValues[]
    totalPages?: number
    totalElements?: number
    numberOfElements?: number
  } | null

  const content = paginated?.content ?? []

  return {
    content,
    totalPages: paginated?.totalPages ?? 1,
    totalElements: paginated?.totalElements ?? content.length,
    numberOfElements: paginated?.numberOfElements ?? content.length,
  }
}

const normalizeEntityListingContent = (data: unknown): FieldValues[] => {
  if (Array.isArray(data)) {
    return data as FieldValues[]
  }

  const paginated = data as { content?: FieldValues[] } | null
  return paginated?.content ?? []
}

const toCommunicationLogCase = (
  caseItem: FieldValues
): CommunicationLogCase | null => {
  const caseKey = caseItem.case_key
  if (!caseKey) return null

  const id = Number(caseItem.id)

  return {
    id: Number.isNaN(id) ? undefined : id,
    case_key: String(caseKey),
  }
}

const dedupeCommunicationLogCases = (
  caseItems: FieldValues[]
): CommunicationLogCase[] => {
  const casesById = new Map<number | string, CommunicationLogCase>()

  for (const caseItem of caseItems) {
    const mappedCase = toCommunicationLogCase(caseItem)
    if (!mappedCase?.case_key) continue

    const dedupeKey = mappedCase.id ?? mappedCase.case_key
    if (casesById.has(dedupeKey)) continue

    casesById.set(dedupeKey, mappedCase)
  }

  return Array.from(casesById.values())
}

export const buildCommunicationsLookupPayload = (
  fieldName: string,
  communicationId: string | number
): GetGenericEntityListingApiType => ({
  entity_name: INTEGRATION_LOGS.COMMUNICATION_ENTITY_NAME,
  select: ['id', 'case_id', 'parent_communication_id', 'case', 'cases'],
  page: 0,
  size: 500,
  filters: [
    {
      logical_operator: 'AND',
      criteria: [
        {
          field_name: fieldName,
          operation: 'EQUALS',
          values: [String(communicationId)],
        },
      ],
    },
  ],
  related_entities: {
    case: {
      select: ['id', 'case_key'],
    },
    cases: {
      select: ['id', 'case_key'],
    },
  },
})

export const fetchCommunicationsByField = async (
  fieldName: string,
  communicationId: string | number
): Promise<FieldValues[]> => {
  const payload = buildCommunicationsLookupPayload(fieldName, communicationId)
  const response = await getGenericEntityListingAPI(payload)
  return normalizeEntityListingContent(response)
}

export const mapRelatedCasesFromCommunications = (
  records: FieldValues[]
): CommunicationLogCase[] => {
  const relatedCases: FieldValues[] = []

  for (const record of records) {
    const singularCase = record?.case
    const pluralCases = record?.cases

    if (Array.isArray(singularCase)) {
      relatedCases.push(...(singularCase as FieldValues[]))
    } else if (singularCase) {
      relatedCases.push(singularCase as FieldValues)
    }

    if (Array.isArray(pluralCases)) {
      relatedCases.push(...(pluralCases as FieldValues[]))
    } else if (pluralCases) {
      relatedCases.push(pluralCases as FieldValues)
    }
  }

  return dedupeCommunicationLogCases(relatedCases)
}

export const fetchParentCasesByResourceId = async (
  resourceId: string | number
): Promise<CommunicationLogCase[]> => {
  try {
    const data = await getCommunicationLogCasesByIdAPI(resourceId)
    const cases = Array.isArray(data) ? data : data?.cases

    if (!Array.isArray(cases)) return []

    return dedupeCommunicationLogCases(cases as FieldValues[])
  } catch (error) {
    console.error(error)
    return []
  }
}

export const fetchAssociatedCasesByResourceId = async (
  resourceId: string | number
): Promise<CommunicationLogCase[]> => {
  const parentCases = await fetchParentCasesByResourceId(resourceId)
  if (parentCases.length) return parentCases

  const communications = await fetchCommunicationsByField(
    INTEGRATION_LOGS.PARENT_COMMUNICATION_ID,
    resourceId
  )
  return mapRelatedCasesFromCommunications(communications)
}

export const copyToClipboard = async (value: string): Promise<void> => {
  if (!value) return

  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(value)
    return
  }

  const textArea: HTMLTextAreaElement = document.createElement('textarea')
  textArea.value = value
  textArea.style.position = 'fixed'
  textArea.style.opacity = '0'
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()
  document.execCommand('copy')
  document.body.removeChild(textArea)
}
